import {IPolicyApplication, ITraveller, IPremium, IQuotes} from '../models';
import * as moment from 'moment';
import * as uuid from 'uuid/v4';
import { getIncludedResource } from '../util';
import { forEach, get } from 'lodash';
import * as document from './documents';

const dateFromApi = (date) => {
  if (! date) {
    return '';
  }

  moment.locale('en-gb');
  const parsedDate = moment.utc(date).startOf('day').format('DD/MM/YYYY');

  return parsedDate !== 'Invalid Date' ? parsedDate : '';
};

const dateToApi = (date) => {
  if (! date) {
    return '';
  }

  const formattedDate = moment(date, 'DD/MM/YYYY').utcOffset(0, true).format();

  return formattedDate !== 'Invalid Date' ? formattedDate : '';
};

export function fromJsonApi(data, included = []): IPolicyApplication {
  const { id, attributes : { metadata } } = data;

  const mapTraveller = (type, traveller: any, key: number): ITraveller => {
    const data: ITraveller = {
      id: traveller.id ? traveller.id : uuid(),
      title: traveller.title,
      firstName: traveller.first_name,
      lastName: traveller.last_name,
      dob: dateFromApi(traveller.dob),
      type,
      screening: traveller.screening,
      screeningSessionId: traveller.screening_id,
    };

    if (key === 0 && type === 'adult') {
      data.email = traveller.email;
      data.phone = traveller.phone1;
      data.address = traveller.address;
    }

    return data;
  };

  const travellers = [];

  const premiums: IPremium[] = getIncludedResource(data, included, 'premiums', true).map((premium): IPremium => {
    const scheme = getIncludedResource(premium, included, 'scheme');
    const documentsResource = getIncludedResource(data, included, 'channel_scheme_documents', true);
    const documents = {};

    documentsResource.forEach((document) => {
      documents[document.attributes.short_name] = document.attributes.file_path;
    });

    return {
      id: premium.id,
      schemeType: scheme.attributes.config?.scheme_type ?? scheme.attributes.scheme_type,
      calculatedAt: premium.attributes.calculated_at,
      validUntil: premium.attributes.valid_until,
      currency: premium.attributes.currency,
      tax: premium.attributes.tax,
      principle: premium.attributes.principle,
      gross: premium.attributes.gross,
      benefits: premium.attributes.benefits.map((benefit) => ({
        name: benefit.name,
        text: benefit.text,
        inverse: benefit.inverse,
        excess: benefit.excess,
        displayName: benefit.display_name,
      })),
      information: premium.attributes.information && {
        medical: premium.attributes.information.medical,
        maxTripDuration: premium.attributes.information.max_trip_duration,
        startDate: premium.attributes.information.start_date,
        region: premium.attributes.information.region,
        tripType: premium.attributes.information.scheme_type,
      },
      errors: get(premium, 'attributes.errors', []),
      declines: get(premium, 'attributes.declines', []),
      referrals: get(premium , 'attributes.referrals', []),
      isSignposted: get(premium, 'attributes.is_signposted', false),
      scheme: {
        id: scheme.id,
        coverLevel: scheme.attributes.cover_level,
        logo: scheme.attributes.logo,
        documents,
      },
    };
  });

  const quotes: IQuotes = {};

  premiums.forEach((premium) => {
    if (!quotes[premium.scheme.coverLevel]) {
      quotes[premium.scheme.coverLevel] = {};
    }

    quotes[premium.scheme.coverLevel][premium.schemeType] = premium;
  });

  metadata.adults.forEach((adult, key) => {
    travellers.push(mapTraveller('adult', adult, key));
  });

  if (metadata.children) {
    metadata.children.forEach((child, key) => {
      travellers.push(mapTraveller('child', child, key));
    });
  }

  const documents = getIncludedResource(data, included, 'documents', true)
    .map((item) => document.fromJsonApi(item, included));

  // noinspection UnnecessaryLocalVariableJS
  const policyApplication: IPolicyApplication = {
    tripType: metadata.scheme_type,
    quoteType: data.attributes.quote_type,
    renewalStatus: data.attributes.renewal_status ? data.attributes.renewal_status : '',
    residency: metadata.residency,
    groupType: metadata.traveller_group,
    destinations: metadata.destinations,
    region: metadata.region,
    servingCountry: metadata.serving_country,
    sendingField: metadata.sending_field,
    sendingOrganisation: metadata.sending_organisation,
    startDate: dateFromApi(metadata.start_date),
    endDate: dateFromApi(metadata.end_date),
    quotationDeclaration: metadata.quotation_declaration,
    travellers,
    steps: metadata.steps,
    maxTripDuration: metadata.max_trip_duration,
    quoteReference: data.attributes.quote_reference,
    activities: metadata.activities,
    calculateAllSchemes: false,
    campaignCode: metadata.campaign_code,
    medicalDeclaration: metadata.medical_declaration_questions,
    options: metadata.options,
    schemeId: metadata.scheme_id,
    status: data.attributes.status ? data.attributes.status : null,
    documents,
    generateDocuments: false,
    channelName: data.attributes.channel_name,
  };

  if (id) {
    policyApplication.id = id;
  }

  if (quotes) {
    policyApplication.quotes = quotes;
  }

  if (metadata.purchase_channel) {
    policyApplication.purchaseChannel = metadata.purchase_channel;
  }

  return policyApplication;
}

export function toJsonApi(policyApplication: IPolicyApplication, channelId?: string, brokerId?: string) {
  const mapTraveller = (type) => (traveller: ITraveller, key: number) => {
    const data: any = {
      id: traveller.id,
      title: traveller.title,
      first_name: traveller.firstName,
      last_name: traveller.lastName,
      dob: dateToApi(traveller.dob),
      screening: traveller.screening,
      screening_id: traveller.screeningSessionId,
      conditions_covered: true,
    };

    if (key === 0 && type === 'adult') {
      data.email = traveller.email;
      data.phone1 = traveller.phone;
      data.address = traveller.address;
    }

    return data;
  };

  const metadata: any = {
    scheme_type: policyApplication.tripType,
    traveller_group: policyApplication.groupType,
    residency: policyApplication.residency,
    destinations: policyApplication.destinations,
    region: policyApplication.region ? policyApplication.region : '',
    serving_country: policyApplication.servingCountry ? policyApplication.servingCountry : '',
    sending_organisation: policyApplication.sendingOrganisation ? policyApplication.sendingOrganisation : '',
    sending_field: policyApplication.sendingField ? policyApplication.sendingField : '',
    start_date: dateToApi(policyApplication.startDate),
    end_date: dateToApi(policyApplication.endDate),
    max_trip_duration: policyApplication.maxTripDuration,
    adults: policyApplication.travellers.filter(({ type }) => type === 'adult').map(mapTraveller('adult')),
    children: policyApplication.travellers.filter(({ type }) => type === 'child').map(mapTraveller('child')),
    options: policyApplication.options,
    medical_declaration_questions: policyApplication.medicalDeclaration,
    quotation_declaration: policyApplication.quotationDeclaration,
    generate_documents: policyApplication.generateDocuments,
    email_send: policyApplication.emailSend,
    steps: policyApplication.steps,
    activities: policyApplication.activities,
    campaign_code: policyApplication.campaignCode,
    scheme_id: policyApplication.schemeId,
    signposted: policyApplication.signposted,
  };

  if (brokerId) {
    metadata.broker_id = brokerId;
  }

  if (policyApplication.purchaseChannel) {
    metadata.purchase_channel = policyApplication.purchaseChannel;
  }

  forEach(policyApplication.options, (value: string | boolean, key) => {
    if (value === 'true') {
      value = true;
    }

    if (value === 'false') {
      value = false;
    }

    metadata.options[key] = value;
  });

  const meta = {
    customer: {
      create_account: policyApplication.createAccount ? policyApplication.createAccount : false,
    },
  };

  // noinspection UnnecessaryLocalVariableJS
  const data: any = {
    type: 'application',
    attributes: {
      metadata,
    },
    meta,
    relationships: {},
  };

  if (policyApplication.schemeId && ! policyApplication.calculateAllSchemes) {
    data.relationships.schemes = {
      data: [
        { id: policyApplication.schemeId },
      ],
    };
  }

  if (policyApplication.id) {
    data.id = policyApplication.id;
  }

  if (channelId) {
    data.relationships.channel = {
      data: {
        id: channelId,
      },
    };
  }

  if (brokerId) {
    data.relationships.broker = {
      data: {
        id: brokerId,
      },
    };
  }

  return  { data, meta };
}
